import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "../supabase";
import SemicircleGauge from "../components/SemicircleGauge";
import DonorWall from "../components/DonorWall";
import ShareableGraphic from "../components/ShareableGraphic";
import defaultAvatar from "../assets/BBall@2x.png";
import Footer from "../components/Footer";

// Animated number that counts up
function AnimatedNumber({ value, prefix = "", duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    if (from === to) {
      setDisplay(to);
      return;
    }
    const start = performance.now();
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    prevRef.current = to;
  }, [value, duration]);

  return <>{prefix}{display.toLocaleString()}</>;
}

export default function ThankYouPage() {
  const { playerId } = useParams();
  const [searchParams] = useSearchParams();
  const donorName = searchParams.get("donor") || "Friend";
  const donationAmount = searchParams.get("amount") || "0";

  const [player, setPlayer] = useState(null);
  const [donations, setDonations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const queries = [
      supabase.from("players").select("*").eq("id", Number(playerId)).single(),
      supabase
        .from("donations")
        .select("*")
        .eq("player_id", Number(playerId))
        .order("created_at", { ascending: false }),
      supabase.from("coach_settings").select("*").limit(1).single(),
    ];

    const [playerRes, donRes, settingsRes] = await Promise.all(queries);
    if (playerRes.data) setPlayer(playerRes.data);
    if (donRes.data) setDonations(donRes.data);
    if (settingsRes.data) setSettings(settingsRes.data);
    setLoading(false);
  }, [playerId]);

  useEffect(() => {
    fetchData();
    // Refetch after delays to catch webhook-recorded donation
    const t1 = setTimeout(fetchData, 2000);
    const t2 = setTimeout(fetchData, 5000);
    const t3 = setTimeout(fetchData, 10000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <p className="text-black/40">Loading...</p>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-black mb-2">
            Page not found
          </h2>
          <Link to="/" className="text-black font-semibold hover:underline">
            Back to team
          </Link>
        </div>
      </div>
    );
  }

  const raised = donations.reduce((sum, d) => sum + Number(d.amount), 0);
  const goal = player.goal_amount;
  const remaining = Math.max(0, goal - raised);
  const photoUrl = player.photo_url || defaultAvatar;
  const playerPageUrl = `${window.location.origin}/player/${player.id}`;

  // Countdown
  const endDate = settings?.end_date
    ? new Date(settings.end_date + "T23:59:59")
    : new Date("2026-06-01T00:00:00");
  const now = new Date();
  const daysLeft = Math.max(
    0,
    Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))
  );

  const donorWallData = donations.map((d) => ({
    id: d.id,
    name: d.donor_name,
    amount: Number(d.amount),
    message: d.message,
    anonymous: d.is_anonymous,
  }));

  // Share handlers
  const shareText = `I just supported #${player.jersey_number} ${player.name} of the Buchtel Griffins Baseball Program! Help them reach their goal`;

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `I supported ${player.name}!`,
          text: shareText,
          url: playerPageUrl,
        });
      } catch (e) {}
    }
  };

  const handleFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(playerPageUrl)}&quote=${encodeURIComponent(shareText)}`,
      "_blank",
      "width=600,height=400"
    );
  };

  const handleTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(playerPageUrl)}`,
      "_blank",
      "width=600,height=400"
    );
  };

  const handleSMS = () => {
    const text = `${shareText} 👉 ${playerPageUrl}`;
    window.open(`sms:?body=${encodeURIComponent(text)}`);
  };

  return (
    <div className="min-h-screen bg-cream pb-16">
      {/* Hero — player photo background */}
      <header className="bg-black text-white relative overflow-hidden">
        {/* Blurred background photo */}
        {player.photo_url && (
          <div
            className="absolute inset-0 opacity-20 blur-2xl scale-110"
            style={{
              backgroundImage: `url(${player.photo_url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}

        <div className="relative max-w-2xl mx-auto px-5 pt-10 pb-14 text-center">
          {/* Success checkmark */}
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-500 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          {/* Player photo */}
          <div className="relative w-28 h-28 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-[4px] border-red-600" />
            <img
              src={photoUrl}
              alt={player.name}
              className={`w-full h-full rounded-full ${
                player.photo_url
                  ? "object-cover"
                  : "object-contain p-3 bg-white/10"
              }`}
            />
            <div className="absolute -bottom-1 -right-1 bg-red-600 text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center border-2 border-black">
              #{player.jersey_number}
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-extrabold mb-2">
            Thank You, {donorName}!
          </h1>
          <p className="text-white/70 text-lg max-w-md mx-auto">
            You just helped{" "}
            <span className="text-white font-semibold">{player.name}</span> get
            closer to the field.
          </p>
          <p className="text-red-400 font-bold text-2xl mt-3">
            ${donationAmount} donated
          </p>
        </div>
      </header>

      {/* Countdown banner */}
      {daysLeft > 0 && (
        <div className="bg-red-600 text-white text-center py-2.5 text-sm font-semibold">
          Only {daysLeft} day{daysLeft !== 1 ? "s" : ""} left in the fundraiser!
        </div>
      )}

      <main className="max-w-2xl mx-auto px-5">
        {/* Progress card */}
        <section className="-mt-6 bg-white rounded-xl border border-warm-gray shadow-sm px-5 pt-5 pb-4 mb-6">
          <SemicircleGauge raised={raised} goal={goal} />
          <p className="text-center text-sm text-black/50 mt-1">
            <AnimatedNumber value={remaining} prefix="$" /> to go — help {player.name} reach the goal!
          </p>
        </section>

        {/* Share section */}
        <section className="mb-6">
          <h2 className="font-display text-lg font-bold text-black mb-3">
            Spread the Word
          </h2>
          <div className="grid grid-cols-4 gap-2">
            {typeof navigator !== "undefined" && navigator.share && (
              <button
                onClick={handleNativeShare}
                className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors col-span-1"
              >
                <svg
                  className="w-5 h-5 text-black"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  />
                </svg>
                <span className="text-[10px] font-medium text-black/60">
                  Share
                </span>
              </button>
            )}
            <button
              onClick={handleFacebook}
              className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors"
            >
              <svg
                className="w-5 h-5 text-black"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span className="text-[10px] font-medium text-black/60">
                Facebook
              </span>
            </button>
            <button
              onClick={handleTwitter}
              className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors"
            >
              <svg
                className="w-5 h-5 text-black"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span className="text-[10px] font-medium text-black/60">
                Post
              </span>
            </button>
            <button
              onClick={handleSMS}
              className="flex flex-col items-center gap-1 py-3 bg-white border border-warm-gray rounded-xl hover:bg-cream transition-colors"
            >
              <svg
                className="w-5 h-5 text-black"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                />
              </svg>
              <span className="text-[10px] font-medium text-black/60">
                Text
              </span>
            </button>
          </div>
        </section>

        {/* Shareable graphic */}
        <section className="mb-6">
          <h2 className="font-display text-lg font-bold text-black mb-3">
            Share Your Support
          </h2>
          <div className="bg-white rounded-xl border border-warm-gray p-5">
            <ShareableGraphic
              player={player}
              amount={donationAmount}
              donorName={donorName}
            />
          </div>
        </section>

        {/* Donor wall */}
        <section className="mb-8">
          <h2 className="font-display text-lg font-bold text-black mb-3">
            {player.name}{player.name.endsWith("s") ? "'" : "'s"} Supporters
            {donorWallData.length > 0 && (
              <span className="text-black/30 font-normal text-sm ml-2">
                ({donorWallData.length})
              </span>
            )}
          </h2>
          <DonorWall donors={donorWallData} />
        </section>

        {/* PSP disclosure */}
        <p className="text-xs text-black/40 text-center mb-4">
          Your donation is made to{" "}
          <a
            href="https://PSPeople.org"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-black/60"
          >
            Providence Serving People
          </a>
          , a nonprofit organization.
        </p>

        {/* Back to player page */}
        <div className="text-center">
          <Link
            to={`/player/${player.id}`}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-10 rounded-full transition-colors text-base shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to {player.name}{player.name.endsWith("s") ? "'" : "'s"} Page
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
